/**
 * Default editor layout sizing and persisted-layout validation (A-20).
 *
 * The reference sizes (Outliner 280, Inspector 340, bottom dock 236 at
 * 1440×900) are the maxima. Smaller windows get proportional sizes with floors,
 * and the viewport always keeps a usable area.
 */

/** How the editor is laid out: the dock (desktop), or the compact layout for phones. */
export type EditorLayout = "desktop" | "compact";

/** Every dockable panel, by id. The layout must contain exactly these. */
export const PANEL_IDS = [
  "outliner",
  "viewport",
  "inspector",
  "contentBrowser",
  "console",
  "statistics",
] as const;
export type PanelId = (typeof PANEL_IDS)[number];

/** Menu bar (28) + toolbar (44) + status bar (24): the fixed chrome around the dock area. */
export const CHROME_HEIGHT = 28 + 44 + 24;

const OUTLINER = { share: 0.195, min: 200, max: 280 };
const INSPECTOR = { share: 0.236, min: 260, max: 340 };
const DOCK = { share: 0.294, min: 160, max: 236 };
const MIN_VIEWPORT_WIDTH = 360;
const MIN_VIEWPORT_HEIGHT = 300;

export interface PanelSizes {
  readonly outliner: number;
  readonly inspector: number;
  /** Height of the bottom dock (Content Browser, Console). */
  readonly dock: number;
}

export function defaultPanelSizes(windowWidth: number, windowHeight: number): PanelSizes {
  let outliner = sized(windowWidth, OUTLINER);
  let inspector = sized(windowWidth, INSPECTOR);

  // Give width back to the viewport if the side panels would crowd it out.
  const overflow = outliner + inspector + MIN_VIEWPORT_WIDTH - windowWidth;
  if (overflow > 0) {
    const shrinkable = outliner - OUTLINER.min + (inspector - INSPECTOR.min);
    const ratio = shrinkable > 0 ? Math.min(1, overflow / shrinkable) : 0;
    outliner -= Math.round((outliner - OUTLINER.min) * ratio);
    inspector -= Math.round((inspector - INSPECTOR.min) * ratio);
  }

  const available = windowHeight - CHROME_HEIGHT;
  const dock = Math.max(
    DOCK.min,
    Math.min(sized(available, DOCK), available - MIN_VIEWPORT_HEIGHT),
  );

  return { outliner, inspector, dock };
}

/** Below this width, half the bottom row is too narrow for the Content Browser's tiles. */
const SPLIT_DOCK_MIN_WIDTH = 1100;

/**
 * Whether the bottom row puts the Content Browser and the Console side by side
 * (half each), or tabs them together on narrower windows.
 */
export function bottomDockSplits(windowWidth: number): boolean {
  return windowWidth >= SPLIT_DOCK_MIN_WIDTH;
}

/** A persisted dockview layout is reused only if it has exactly the editor's panels. */
export function isUsableLayout(layout: unknown): boolean {
  if (typeof layout !== "object" || layout === null) return false;
  const panels = (layout as { panels?: unknown }).panels;
  if (typeof panels !== "object" || panels === null) return false;

  const ids = Object.keys(panels);
  return ids.length === PANEL_IDS.length && PANEL_IDS.every((id) => ids.includes(id));
}

function sized(total: number, rule: { share: number; min: number; max: number }): number {
  return Math.max(rule.min, Math.min(rule.max, Math.round(total * rule.share)));
}
